/**
 * Post formatting — a small, safe subset (owner, 2026-10-01: option A).
 *
 * A post is stored as text with marks; this turns it into a tree of plain
 * nodes that the panel draws as React elements. **No HTML is ever produced or
 * parsed** — angle brackets in a post are text, and a link is only an `http(s)`
 * URL — so nothing a member or an Open House visitor types can run.
 *
 *     # Big heading     ## Medium heading     ### Small heading
 *     **bold**   *italic*   {green}coloured{/green}   {small}small grey note{/small}
 *     - bullet      1. numbered      > quote      [text](https://…)   bare https://…
 *     $NVDA cashtags still link to charts
 *
 * **Colours are the theme's, never a hex** (D13): green · red · amber · blue ·
 * grey, each resolved to a token, so a post reads on every theme.
 */

import { tokenize } from "./community";

export const POST_COLOURS = ["green", "red", "amber", "blue", "grey"] as const;
export type PostColour = (typeof POST_COLOURS)[number];

/** Each colour as a theme CSS variable — what makes it follow dark, light and custom themes. */
export const COLOUR_VAR: Record<PostColour, string> = {
  green: "var(--qf-positive)",
  red: "var(--qf-negative)",
  amber: "var(--qf-warn)",
  blue: "var(--qf-accent)",
  grey: "var(--qf-text-secondary)",
};

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "cashtag"; symbol: string }
  | { kind: "link"; href: string; children: Inline[] }
  | { kind: "bold"; children: Inline[] }
  | { kind: "italic"; children: Inline[] }
  | { kind: "colour"; colour: PostColour; children: Inline[] }
  | { kind: "small"; children: Inline[] };

export type Block =
  | { kind: "heading"; level: 1 | 2 | 3; children: Inline[] }
  | { kind: "paragraph"; lines: Inline[][] }
  | { kind: "list"; ordered: boolean; items: Inline[][] }
  | { kind: "quote"; lines: Inline[][] };

/** Only web links. `javascript:`, `data:` and friends are text. */
export function safeHref(href: string): string | null {
  return /^https?:\/\/[^\s<>"']+$/i.test(href) ? href : null;
}

// ── inline ──────────────────────────────────────────────────────────────────

/** Text with no marks left in it: cashtags and bare links. */
function leaf(text: string): Inline[] {
  return tokenize(text).map((seg) =>
    seg.kind === "text"
      ? { kind: "text", text: seg.text }
      : seg.kind === "cashtag"
        ? { kind: "cashtag", symbol: seg.symbol }
        : { kind: "link", href: seg.href, children: [{ kind: "text", text: seg.href }] },
  );
}

/**
 * The marks, tried in this order at each position. A mark with no closing pair
 * is text — an unmatched `*` in "5*3" must not italicise the rest of the post.
 */
const INLINE: { re: RegExp; make: (m: RegExpExecArray) => Inline | null }[] = [
  {
    re: /\[([^\]\n]{1,200})\]\(([^)\s]{1,500})\)/y,
    make: (m) => {
      const href = safeHref(m[2]!);
      return href ? { kind: "link", href, children: parseInline(m[1]!) } : null;
    },
  },
  {
    re: /\{(green|red|amber|blue|grey)\}([\s\S]{1,2000}?)\{\/\1\}/y,
    make: (m) => ({ kind: "colour", colour: m[1] as PostColour, children: parseInline(m[2]!) }),
  },
  { re: /\{small\}([\s\S]{1,2000}?)\{\/small\}/y, make: (m) => ({ kind: "small", children: parseInline(m[1]!) }) },
  { re: /\*\*(?=\S)([\s\S]{1,2000}?\S)\*\*/y, make: (m) => ({ kind: "bold", children: parseInline(m[1]!) }) },
  { re: /\*(?=[^\s*])([^*\n]{1,2000}?[^\s*]|[^\s*])\*/y, make: (m) => ({ kind: "italic", children: parseInline(m[1]!) }) },
];

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let plain = "";
  let i = 0;
  const flush = () => {
    if (plain) out.push(...leaf(plain));
    plain = "";
  };
  while (i < text.length) {
    const c = text[i]!;
    let matched = false;
    if (c === "[" || c === "{" || c === "*") {
      for (const rule of INLINE) {
        rule.re.lastIndex = i;
        const m = rule.re.exec(text);
        if (!m) continue;
        const node = rule.make(m);
        if (!node) continue;
        flush();
        out.push(node);
        i += m[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      plain += c;
      i += 1;
    }
  }
  flush();
  return out;
}

// ── blocks ──────────────────────────────────────────────────────────────────

const HEADING = /^(#{1,3})\s+(.+)$/;
const BULLET = /^\s*[-•]\s+(.*)$/;
const NUMBERED = /^\s*\d{1,3}[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;

/** A post's text as blocks. Blank lines separate paragraphs; single breaks are kept. */
export function parsePost(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      blocks.push({ kind: "heading", level: h[1]!.length as 1 | 2 | 3, children: parseInline(h[2]!.trim()) });
      i += 1;
      continue;
    }
    if (BULLET.test(line) || NUMBERED.test(line)) {
      const ordered = !BULLET.test(line);
      const pattern = ordered ? NUMBERED : BULLET;
      const items: Inline[][] = [];
      while (i < lines.length && pattern.test(lines[i]!)) {
        items.push(parseInline(pattern.exec(lines[i]!)![1]!));
        i += 1;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }
    if (QUOTE.test(line)) {
      const quoted: Inline[][] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) {
        quoted.push(parseInline(QUOTE.exec(lines[i]!)![1]!));
        i += 1;
      }
      blocks.push({ kind: "quote", lines: quoted });
      continue;
    }
    const para: Inline[][] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !HEADING.test(lines[i]!) &&
      !BULLET.test(lines[i]!) &&
      !NUMBERED.test(lines[i]!) &&
      !QUOTE.test(lines[i]!)
    ) {
      para.push(parseInline(lines[i]!));
      i += 1;
    }
    blocks.push({ kind: "paragraph", lines: para });
  }
  return blocks;
}

/** The text with its marks taken out — for a one-line preview. */
export function plainText(text: string): string {
  return text
    .replace(/\[([^\]\n]+)\]\((https?:[^)\s]+)\)/g, "$1")
    .replace(/\{\/?(green|red|amber|blue|grey|small)\}/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1$2")
    .replace(/^#{1,3}\s+/gm, "")
    .replace(/^\s*(?:[-•]|\d{1,3}[.)])\s+/gm, "")
    .replace(/^>\s?/gm, "");
}

// ── the toolbar's edits ─────────────────────────────────────────────────────

export interface Edit {
  text: string;
  selStart: number;
  selEnd: number;
}

/** Wrap the selection in a pair of marks; with nothing selected, insert the pair and a placeholder. */
export function wrap(text: string, start: number, end: number, open: string, close: string, placeholder = "text"): Edit {
  const inner = text.slice(start, end) || placeholder;
  const next = text.slice(0, start) + open + inner + close + text.slice(end);
  return { text: next, selStart: start + open.length, selEnd: start + open.length + inner.length };
}

/** Put a prefix at the start of every line the selection touches (headings, lists, quotes). */
export function prefixLines(text: string, start: number, end: number, prefix: (n: number) => string): Edit {
  const lineStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
  const nextBreak = text.indexOf("\n", end);
  const lineEnd = nextBreak === -1 ? text.length : nextBreak;
  const block = text.slice(lineStart, lineEnd);
  const replaced = block
    .split("\n")
    .map((l, n) => prefix(n) + l.replace(/^(#{1,3}\s+|[-•]\s+|\d{1,3}[.)]\s+|>\s?)/, ""))
    .join("\n");
  const next = text.slice(0, lineStart) + replaced + text.slice(lineEnd);
  return { text: next, selStart: lineStart, selEnd: lineStart + replaced.length };
}
